import React, { useEffect, useState } from 'react'
import axios from 'axios'
import { toast } from 'react-hot-toast'
import { Copy, Check, Eye, EyeOff, Building2, Terminal, Cpu } from 'lucide-react'
import { Link } from 'react-router-dom'
import { getSession } from '../lib/session'
import UserVariables from '../components/UserVariables'
import McpServers from '../components/McpServers'

// "Workspace" — the signed-in tenant's account page. Workspaces are created through
// sign-in (Google / email), so there is no create-tenant form here anymore; this shows
// the current workspace, its plan + included services, and the developer API key.
export default function TenantSetup() {
  const session = getSession()
  const [products, setProducts] = useState(null)
  const [copied, setCopied] = useState(false)
  const [showKey, setShowKey] = useState(false)
  const [cliToken, setCliToken] = useState(null)
  const [cliCopied, setCliCopied] = useState(false)
  const [generating, setGenerating] = useState(false)

  const generateCliToken = async () => {
    setGenerating(true)
    try {
      const r = await axios.post('/api/v1/auth/cli-token')
      setCliToken(r.data.token)
      toast.success(`CLI token generated (valid ${r.data.expiresInDays} days)`)
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not generate token')
    } finally { setGenerating(false) }
  }
  const copyCli = () => { navigator.clipboard.writeText(cliToken || ''); setCliCopied(true); setTimeout(() => setCliCopied(false), 2000) }

  // Real programmatic API key (X-Api-Key). Only the hash is stored server-side;
  // the plaintext is shown once at generation.
  const [apiKeyConfigured, setApiKeyConfigured] = useState(false)
  const [newApiKey, setNewApiKey] = useState(null)
  const [apiBusy, setApiBusy] = useState(false)
  const [apiCopied, setApiCopied] = useState(false)
  const loadApiKey = () => {
    if (!session?.namespaceId) return
    axios.get(`/api/v1/tenants/${session.namespaceId}/api-key`).then(r => setApiKeyConfigured(!!r.data.configured)).catch(() => {})
  }
  useEffect(loadApiKey, [session?.namespaceId])
  const generateApiKey = async () => {
    if (apiKeyConfigured && !window.confirm('Rotate the API key? The current one stops working immediately.')) return
    setApiBusy(true)
    try {
      const r = await axios.post(`/api/v1/tenants/${session.namespaceId}/api-key`)
      setNewApiKey(r.data.apiKey); setApiKeyConfigured(true)
      toast.success('API key generated — copy it now, it won’t be shown again')
    } catch (err) { toast.error(err.response?.data?.error || 'Failed') } finally { setApiBusy(false) }
  }
  const revokeApiKey = async () => {
    if (!window.confirm('Revoke the API key? Anything using it stops working.')) return
    try { await axios.delete(`/api/v1/tenants/${session.namespaceId}/api-key`); setApiKeyConfigured(false); setNewApiKey(null); toast.success('Revoked') }
    catch { toast.error('Failed') }
  }
  const copyApi = () => { navigator.clipboard.writeText(newApiKey || ''); setApiCopied(true); setTimeout(() => setApiCopied(false), 2000) }

  // Model provider (bring your own AWS Bedrock / Azure OpenAI / OpenAI-compatible).
  const [mp, setMp] = useState(null)              // current (masked) from server
  const [mpForm, setMpForm] = useState({ provider: 'openai' })
  const [mpBusy, setMpBusy] = useState(false)
  const loadMp = () => {
    if (!session?.namespaceId) return
    axios.get(`/api/v1/tenants/${session.namespaceId}/model-provider`).then(r => setMp(r.data)).catch(() => setMp(null))
  }
  useEffect(loadMp, [session?.namespaceId])
  const saveMp = async () => {
    setMpBusy(true)
    try {
      await axios.put(`/api/v1/tenants/${session.namespaceId}/model-provider`, mpForm)
      toast.success('Model provider saved — your AI answers now use it')
      setMpForm({ provider: 'openai' }); loadMp()
    } catch (err) { toast.error(err.response?.data?.error || 'Save failed') } finally { setMpBusy(false) }
  }
  const clearMp = async () => {
    setMpBusy(true)
    try { await axios.delete(`/api/v1/tenants/${session.namespaceId}/model-provider`); toast.success('Reverted to the platform default'); loadMp() }
    catch (err) { toast.error('Failed') } finally { setMpBusy(false) }
  }
  const mpF = (k, v) => setMpForm(f => ({ ...f, [k]: v }))

  useEffect(() => {
    if (!session?.namespaceId) return
    axios.get(`/api/v1/tenants/${session.namespaceId}/services`)
      .then(r => setProducts((r.data.services || []).filter(s => s.activated).map(s => s.title || s.service)))
      .catch(() => setProducts([]))
  }, [session?.namespaceId])

  const copyKey = () => {
    navigator.clipboard.writeText(session?.apiKey || '')
    setCopied(true); setTimeout(() => setCopied(false), 2000)
    toast.success('API key copied')
  }

  if (!session?.namespaceId) {
    return <div className="max-w-2xl"><p className="text-gray-500">No active workspace.</p></div>
  }

  const maskedKey = session.apiKey
    ? `${session.apiKey.slice(0, 12)}${'•'.repeat(18)}${session.apiKey.slice(-6)}`
    : ''

  return (
    <div className="max-w-2xl">
      <h2 className="text-2xl font-bold text-gray-900">Workspace</h2>
      <p className="text-gray-500 mt-1">Your organization on the platform.</p>

      {/* Current workspace */}
      <div className="card mt-6">
        <div className="flex items-center gap-2 mb-4">
          <Building2 size={18} className="text-gray-500" />
          <span className="font-semibold text-gray-900">{session.name || 'Your workspace'}</span>
          <span className="ml-auto text-[11px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full bg-primary-500/10 text-primary-600 border border-primary-200">
            à-la-carte
          </span>
        </div>
        <dl className="grid grid-cols-[120px_1fr] gap-y-2 text-sm">
          <dt className="text-gray-500">Namespace</dt>
          <dd className="text-gray-900 font-mono text-xs">{session.namespaceId}</dd>
          <dt className="text-gray-500">Billing</dt>
          <dd className="text-gray-900">Pay per service · <Link to="/billing" className="text-primary-600 hover:underline">see billing</Link></dd>
        </dl>
      </div>

      {/* Active services */}
      <div className="card mt-6">
        <h3 className="font-semibold text-gray-900 mb-1">Active services</h3>
        <p className="text-xs text-gray-500 mb-3">The services you've turned on. <Link to="/" className="text-primary-600 hover:underline">Add more →</Link></p>
        {products === null ? (
          <p className="text-sm text-gray-500">Loading your entitlements…</p>
        ) : products.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {products.map((p) => (
              <span key={typeof p === 'string' ? p : (p.name || p.product_type)}
                className="text-xs px-2.5 py-1 rounded-full bg-gray-50 border border-gray-200 text-gray-700">
                {typeof p === 'string' ? p : (p.name || p.product_type)}
              </span>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500">No services active yet. <Link to="/" className="text-primary-600 hover:underline">Browse services →</Link></p>
        )}
      </div>

      {/* Programmatic API key */}
      <div className="card mt-6">
        <h3 className="font-semibold text-gray-900 mb-1">API key</h3>
        <p className="text-xs text-gray-500 mb-3">
          A stable key for calling the API from your servers — send it as the <code className="bg-gray-100 px-1 rounded">X-Api-Key</code> header.
          Only its hash is stored; you'll see the key once. {apiKeyConfigured ? 'A key is currently active.' : 'No key yet.'}
        </p>
        {newApiKey ? (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <code className="flex-1 truncate bg-gray-50 border border-gray-200 rounded px-2.5 py-2 text-xs text-gray-800 font-mono">{newApiKey}</code>
              <button onClick={copyApi} className="p-2 rounded hover:bg-gray-200 text-gray-600" title="Copy">
                {apiCopied ? <Check size={16} /> : <Copy size={16} />}
              </button>
            </div>
            <p className="text-[11px] text-amber-600">Copy it now — it won't be shown again.</p>
          </div>
        ) : (
          <div className="flex items-center gap-3">
            <button onClick={generateApiKey} disabled={apiBusy} className="btn-primary text-sm disabled:opacity-60">
              {apiBusy ? 'Generating…' : apiKeyConfigured ? 'Rotate API key' : 'Generate API key'}
            </button>
            {apiKeyConfigured && <button onClick={revokeApiKey} className="text-sm text-red-600 hover:underline">Revoke</button>}
          </div>
        )}
      </div>

      {/* CLI access */}
      <div className="card mt-6">
        <h3 className="font-semibold text-gray-900 mb-1 flex items-center gap-2">
          <Terminal size={18} className="text-gray-500" /> CLI access
        </h3>
        <p className="text-xs text-gray-500 mb-3">
          Build, ingest and query from your own code with the <code className="text-gray-700">reright</code> CLI.
          It talks to the same API as this console — full parity. Generate a long-lived, <b>company-scoped</b> token
          (valid for this workspace only).
        </p>

        {!cliToken ? (
          <button onClick={generateCliToken} disabled={generating} className="btn-primary text-sm disabled:opacity-60">
            {generating ? 'Generating…' : 'Generate CLI token'}
          </button>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <code className="flex-1 truncate bg-gray-50 border border-gray-200 rounded px-2.5 py-2 text-xs text-gray-800 font-mono">{cliToken}</code>
              <button onClick={copyCli} className="p-2 rounded hover:bg-gray-200 text-gray-600" title="Copy">
                {cliCopied ? <Check size={16} /> : <Copy size={16} />}
              </button>
            </div>
            <p className="text-[11px] text-amber-600">Copy it now — treat it like a password. It's scoped to this company only.</p>
          </div>
        )}

        <div className="mt-4">
          <p className="text-xs font-medium text-gray-600 mb-1">Get started</p>
          <pre className="bg-gray-900 text-gray-100 p-3 rounded-lg text-xs overflow-x-auto"><code>{`npm install -g @reright/cli
reright login --token <YOUR_TOKEN>
reright services
reright rag create "Product Docs" --strategy recursive --metric cosine
reright rag ingest <collectionId> --file ./docs.md
reright rag query  <collectionId> "how do refunds work?"`}</code></pre>
        </div>
      </div>

      {/* Model provider */}
      <div className="card mt-6">
        <h3 className="font-semibold text-gray-900 mb-1 flex items-center gap-2">
          <Cpu size={18} className="text-gray-500" /> Model provider
        </h3>
        <p className="text-xs text-gray-500 mb-3">
          Which AI runs your answers. Default is the platform model. Bring your own <b>AWS Bedrock</b>,
          <b> Azure OpenAI</b>, or any <b>OpenAI-compatible</b> endpoint — keys are encrypted in your vault.
        </p>

        <div className="mb-3 text-sm">
          <span className="text-gray-500">Current: </span>
          <span className="font-medium text-gray-900">{mp?.provider ? mp.provider : 'Platform default (OpenRouter)'}</span>
          {mp?.provider && <button onClick={clearMp} disabled={mpBusy} className="ml-3 text-xs text-red-600 hover:underline">revert to default</button>}
        </div>

        <div className="rounded-lg border border-gray-200 p-4 bg-gray-50/60 space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Provider</label>
            <select className="input" value={mpForm.provider} onChange={e => setMpForm({ provider: e.target.value })}>
              <option value="openai">OpenAI-compatible (OpenAI / OpenRouter / custom)</option>
              <option value="azure">Azure OpenAI</option>
              <option value="bedrock">AWS Bedrock (Claude)</option>
            </select>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {mpForm.provider === 'openai' && <>
              <input className="input" type="password" placeholder="API key" value={mpForm.apiKey || ''} onChange={e => mpF('apiKey', e.target.value)} />
              <input className="input" placeholder="Base URL (optional)" value={mpForm.baseURL || ''} onChange={e => mpF('baseURL', e.target.value)} />
              <input className="input sm:col-span-2" placeholder="Model (e.g. gpt-4.1 or openai/gpt-4o)" value={mpForm.model || ''} onChange={e => mpF('model', e.target.value)} />
            </>}
            {mpForm.provider === 'azure' && <>
              <input className="input" placeholder="Endpoint — https://xxx.openai.azure.com" value={mpForm.endpoint || ''} onChange={e => mpF('endpoint', e.target.value)} />
              <input className="input" type="password" placeholder="API key" value={mpForm.apiKey || ''} onChange={e => mpF('apiKey', e.target.value)} />
              <input className="input" placeholder="Deployment name" value={mpForm.deployment || ''} onChange={e => mpF('deployment', e.target.value)} />
              <input className="input" placeholder="API version (default 2024-06-01)" value={mpForm.apiVersion || ''} onChange={e => mpF('apiVersion', e.target.value)} />
            </>}
            {mpForm.provider === 'bedrock' && <>
              <input className="input" placeholder="AWS region (e.g. us-east-1)" value={mpForm.region || ''} onChange={e => mpF('region', e.target.value)} />
              <input className="input" placeholder="Model id — anthropic.claude-3-5-sonnet-…" value={mpForm.model || ''} onChange={e => mpF('model', e.target.value)} />
              <input className="input" placeholder="Access key ID" value={mpForm.accessKeyId || ''} onChange={e => mpF('accessKeyId', e.target.value)} />
              <input className="input" type="password" placeholder="Secret access key" value={mpForm.secretAccessKey || ''} onChange={e => mpF('secretAccessKey', e.target.value)} />
            </>}
          </div>
          <button onClick={saveMp} disabled={mpBusy} className="btn-primary disabled:opacity-60">
            {mpBusy ? 'Saving…' : 'Use this provider'}
          </button>
        </div>
      </div>

      <UserVariables />
      <McpServers />
    </div>
  )
}
